import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { SEARCH_CHARS_MAX_LIMIT, SEARCH_CHARS_MIN_LIMIT, SEARCH_RESULTS_LIMIT } from '../consts';
import { useOfferCategoryQuery } from '../queries/useOfferCategoryQuery';
import { useSearchOfferCategoriesQuery } from '../queries/useSearchOfferCategories';
import { useVerticalOfferCategoriesQuery } from '../queries/useVerticalOfferCategoriesQuery';
export const useProductCategory = ({ offerType, initialCategoryId, onCategorySelect, }) => {
    const categoryType = offerType === 'DIGITAL' ? 'DIGITAL' : 'PHYSICAL';
    const [optimisticInitialCategory, setOptimisticInitialCategory] = useState();
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue] = useDebounce(searchValue, 500);
    const [categoryId, setCategoryId] = useState(initialCategoryId);
    // Query for initial category data
    const { offerCategoryQuery: initialCategoryQuery } = useOfferCategoryQuery([initialCategoryId, { type: categoryType, depth: 2 }], {
        enabled: !!initialCategoryId,
        staleTime: Infinity,
    });
    // Query for selected category data
    const { offerCategoryQuery: selectedCategoryQuery } = useOfferCategoryQuery([categoryId, { type: categoryType, depth: 2 }], {
        enabled: !!categoryId,
        staleTime: Infinity,
    });
    // Query for top-level categories
    const { verticalOfferCategoriesQuery } = useVerticalOfferCategoriesQuery([
        {
            type: categoryType,
            depth: 1,
        },
    ]);
    // Query for subcategories of selected category
    const { offerCategoryQuery: childrenCategoryQuery } = useOfferCategoryQuery([selectedCategoryQuery.data?.id, { type: categoryType, depth: 2 }], {
        enabled: !!selectedCategoryQuery.data?.id,
        staleTime: Infinity,
    });
    // Query for parent category when needed
    const { offerCategoryQuery: parentCategoryQuery } = useOfferCategoryQuery([childrenCategoryQuery.data?.parentId || '', { type: categoryType, depth: 2 }], {
        enabled: !!selectedCategoryQuery.data?.id &&
            !!childrenCategoryQuery.data?.parentId &&
            childrenCategoryQuery.data?.children.length === 0,
        staleTime: Infinity,
    });
    // Search query for finding categories
    const { searchOfferCategoriesQuery } = useSearchOfferCategoriesQuery([
        {
            search: debouncedSearchValue,
            limit: SEARCH_RESULTS_LIMIT,
            type: categoryType,
        },
    ], {
        enabled: debouncedSearchValue.length >= SEARCH_CHARS_MIN_LIMIT &&
            debouncedSearchValue.length < SEARCH_CHARS_MAX_LIMIT,
        staleTime: Infinity,
        cacheTime: Infinity,
    });
    const initialCategory = useMemo(() => {
        if (optimisticInitialCategory?.id === initialCategoryId && initialCategoryQuery.isFetching) {
            return optimisticInitialCategory;
        }
        return initialCategoryQuery.data;
    }, [
        optimisticInitialCategory,
        initialCategoryId,
        initialCategoryQuery.isFetching,
        initialCategoryQuery.data,
    ]);
    const options = useMemo(() => {
        // Show search results if searching
        if (searchValue.length >= SEARCH_CHARS_MIN_LIMIT &&
            debouncedSearchValue.length >= SEARCH_CHARS_MIN_LIMIT) {
            return (searchOfferCategoriesQuery.data?.filter((categoryItem) => categoryItem.allowSelection) || []);
        }
        // Show subcategories of selected category or siblings if no children
        if (selectedCategoryQuery.data?.id) {
            if (childrenCategoryQuery.data && childrenCategoryQuery.data?.children?.length > 0) {
                // prepend current category if it allows selection
                if (childrenCategoryQuery.data?.allowSelection) {
                    return [
                        { ...childrenCategoryQuery.data, children: [] },
                        ...childrenCategoryQuery.data.children,
                    ];
                }
                return childrenCategoryQuery.data.children;
            }
            if (childrenCategoryQuery.data?.parentId) {
                // prepend current category if it allows selection
                if (parentCategoryQuery.data?.allowSelection) {
                    return [
                        { ...parentCategoryQuery.data, children: [] },
                        ...(parentCategoryQuery.data?.children || []),
                    ];
                }
                return parentCategoryQuery.data?.children || [];
            }
        }
        // Default to showing root categories
        return verticalOfferCategoriesQuery.data || [];
    }, [
        searchValue,
        debouncedSearchValue,
        selectedCategoryQuery.data,
        childrenCategoryQuery.data,
        parentCategoryQuery.data,
        verticalOfferCategoriesQuery.data,
        searchOfferCategoriesQuery.data,
    ]);
    const handleSelectCategory = useCallback((category) => {
        if (!category?.children?.length) {
            setOptimisticInitialCategory(category);
            onCategorySelect(category?.id || '');
        }
        setCategoryId(category?.id || '');
        setSearchValue('');
    }, []);
    const handleReset = useCallback(() => {
        setOptimisticInitialCategory(undefined);
        onCategorySelect('');
        setCategoryId('');
        setSearchValue('');
    }, []);
    return {
        initialCategory,
        selectedCategory: selectedCategoryQuery.data,
        parentCategory: parentCategoryQuery.data,
        selectCategory: handleSelectCategory,
        options,
        areOptionsLoading: selectedCategoryQuery.isFetching ||
            childrenCategoryQuery.isFetching ||
            searchOfferCategoriesQuery.isFetching,
        search: {
            value: searchValue,
            debouncedValue: debouncedSearchValue,
            onChange: setSearchValue,
        },
        reset: handleReset,
    };
};
